import {
  useChecklist,
  useChecklistItemResponseSchema,
  type ChecklistResponse
} from "@swivu/client-api";
import { useChecklistContext } from "./ChecklistContext.js";

export type ChecklistData = ChecklistResponse;
export type ChecklistItem = ChecklistResponse["items"][number];

type UseChecklistCheckmark = {
  item: ChecklistItem;
  handleChecked: (
    item: { checklist_item_id: number; status: 1 | 2 },
    e: PointerEvent
  ) => void;
};

const useChecklistCheckmark = ({
  handleChecked,
  item
}: UseChecklistCheckmark) => {
  const { queryClient, ...props } = useChecklistContext();
  const { checklist_id, slug, api } =
    props.api === "wf"
      ? { api: props.api, checklist_id: props.checklist_id, slug: null }
      : { api: props.api, slug: props.slug, checklist_id: props.checklist_id };

  // Create the query key parameters that match how the data was originally fetched
  const queryKeyParams =
    api === "wf" ? { api, checklist_id } : { api, checklist_id, slug };
  const getKeyArg = { variables: queryKeyParams } as Parameters<
    typeof useChecklist.getKey
  >[0];

  const result = useChecklistItemResponseSchema({
    onMutate: async (variables) => {
      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: useChecklist.getKey(getKeyArg)
      });

      // Snapshot the previous value
      const previousChecklist = queryClient.getQueryData(
        useChecklist.getKey(getKeyArg)
      );
      // Optimistically update to the new value
      queryClient.setQueryData(
        useChecklist.getKey(getKeyArg),
        (old: ChecklistData | undefined) => {
          // console.log({ old, variables });
          if (!old) return old;
          return {
            ...old,
            items: old.items.map((checklistItem) => {
              if (checklistItem.id === variables.id) {
                return { ...checklistItem, status: variables.status };
              }
              return checklistItem;
            })
          };
        }
      );

      // Return a function that will revert the optimistic update if the mutation fails
      return { previousChecklist };
    },
    onError: (_error, _variables, context) => {
      const ctx = context as
        | { previousChecklist: ChecklistData | undefined }
        | undefined;
      if (ctx?.previousChecklist) {
        queryClient.setQueryData(
          useChecklist.getKey(getKeyArg),
          ctx.previousChecklist
        );
      }
    },
    // Always refetch after error or success:
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: useChecklist.getKey(getKeyArg)
      });
    }
  });

  const isChecked = result.isPending
    ? result.variables?.status === 2
    : item.status === 2;

  const handleMutate = async (e: PointerEvent) => {
    if (!result.isPending) {
      if (api === "public") {
        // decision to not allow public api to update checklist items.  Uncomment to change.
        // result.mutate({
        //   api,
        //   id: item.id,
        //   status: isChecked ? 1 : 2,
        //   slug
        // });
      } else {
        result.mutate({
          api,
          id: item.id,
          status: isChecked ? 1 : 2
        });
      }
      handleChecked(
        {
          checklist_item_id: item.id,
          status: isChecked ? 1 : 2
        },
        e
      );
    }
  };
  return { ...result, handleMutate, isChecked };
};

export default useChecklistCheckmark;
